/* global gdAlertify */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import HonorariosPrueba from "./HonorariosPrueba";
import CobranzaPrueba from "./CobranzaPrueba";
import { clavesDe, valoresParaClaves, variablesParaEnvio } from "./honorariosWsp";
import { BadgeEstado, mensajeError } from "./enviosUi";

const MAX_DESTINATARIOS = 2;
const MAX_IMAGEN_MB = 5;
// Tras enviar, se consulta el estado cada 5 s durante 2 min (entregado / leído llegan por webhook).
const SEGUIMIENTO_MS = 5000;
const SEGUIMIENTO_VECES = 24;

const CUERPO_EMAIL_DEMO = "Hola,\n\nEste es un correo de prueba del Motor de Envíos de SK Medical Care.\nSi lo recibiste, el canal está funcionando.\n\nSaludos.";
const TEXTO_WSP_DEMO = "Hola, este es un mensaje de prueba del Motor de Envíos de SK Medical Care.";


const componente = (plantilla, tipo) => (plantilla?.componentes || []).find((c) => String(c.type).toUpperCase() === tipo);

const EnviosPrueba = ({ parametros, onEnviado }) => {
    const [canales, setCanales] = useState([]);
    const [canalId, setCanalId] = useState("");
    const [destinatarios, setDestinatarios] = useState(["", ""]);
    // Correo
    const [asunto, setAsunto] = useState("Prueba Motor de Envíos SKMC");
    const [cuerpo, setCuerpo] = useState(CUERPO_EMAIL_DEMO);
    const [plantillaBase, setPlantillaBase] = useState(true);
    // WhatsApp
    const [modo, setModo] = useState("plantilla");
    const [plantillas, setPlantillas] = useState([]);
    const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
    const [plantillaClave, setPlantillaClave] = useState("");
    const [variables, setVariables] = useState([]);
    const [textoLibre, setTextoLibre] = useState(TEXTO_WSP_DEMO);
    const [imagen, setImagen] = useState(null);
    const [imagenUrl, setImagenUrl] = useState("");
    // Datos reales de honorarios de una auxiliar (imagen + variables) para probar la plantilla
    const [usarHonorarios, setUsarHonorarios] = useState(false);
    const [honorarios, setHonorarios] = useState(null);
    // Correo de cobranza de un paciente (tipo, texto con variables y PDF del detalle)
    const [usarCobranza, setUsarCobranza] = useState(false);
    const [cobranza, setCobranza] = useState(null);
    // Resultado
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const seguimiento = useRef(null);
    const resultadoRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axiosInstance.get("/envios/canales");
                const lista = data?.data || [];
                setCanales(lista);
                const inicial = lista.find((c) => Number(c.activo) === 1) || lista[0];
                if (inicial) setCanalId(String(inicial.id));
            } catch (error) {
                gdAlertify.notiError(mensajeError(error, "No se pudieron cargar los canales."));
            }
        })();
        return () => clearInterval(seguimiento.current);
    }, []);

    const canal = canales.find((c) => String(c.id) === String(canalId));
    const esWsp = canal?.tipo === "whatsapp";
    const plantilla = plantillas.find((p) => `${p.nombre}|${p.idioma}` === plantillaClave);
    const header = componente(plantilla, "HEADER");
    const body = componente(plantilla, "BODY");
    const footer = componente(plantilla, "FOOTER");
    const claves = clavesDe(body?.text);
    const headerConImagen = String(header?.format || "").toUpperCase() === "IMAGE";
    const headerNoSoportado = header && !["IMAGE", "TEXT"].includes(String(header.format || "").toUpperCase());

    // Precarga el destino demo del tipo de canal (correo o WhatsApp) si el campo está vacío
    // o tiene el demo del otro tipo (al cambiar de canal).
    const demoCanal = esWsp ? parametros?.demo_telefono : parametros?.demo_email;
    const demoOtro = esWsp ? parametros?.demo_email : parametros?.demo_telefono;
    useEffect(() => {
        if (!canal) return;
        setDestinatarios((prev) => (!prev[0] || prev[0] === demoOtro ? [demoCanal || "", prev[1]] : prev));
    }, [canal, demoCanal, demoOtro]);

    const vistaImagen = useMemo(
        () => (imagen ? URL.createObjectURL(imagen) : imagenUrl || (usarHonorarios && honorarios?.miniatura) || null),
        [imagen, imagenUrl, usarHonorarios, honorarios]
    );
    useEffect(() => () => { if (imagen && vistaImagen) URL.revokeObjectURL(vistaImagen); }, [imagen, vistaImagen]);

    const cambiarCanal = (id) => {
        setCanalId(id);
        setResultado(null);
        setPlantillas([]);
        setPlantillaClave("");
    };

    const cargarPlantillas = async () => {
        setCargandoPlantillas(true);
        try {
            const { data } = await axiosInstance.get(`/envios/canales/${canalId}/plantillas`);
            const lista = data?.data || [];
            setPlantillas(lista);
            if (!lista.length) gdAlertify.notiWarning("La cuenta de YCloud no tiene plantillas.");
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudieron cargar las plantillas."));
        } finally {
            setCargandoPlantillas(false);
        }
    };

    /** Variables vacías del largo de la plantilla, completadas en orden con los datos de honorarios si hay. */
    const variablesPara = (p, datos) => valoresParaClaves(clavesDe(componente(p, "BODY")?.text), datos?.variables);

    const elegirPlantilla = (clave) => {
        setPlantillaClave(clave);
        const p = plantillas.find((x) => `${x.nombre}|${x.idioma}` === clave);
        setVariables(variablesPara(p, honorarios));
    };

    // La imagen de honorarios la genera el backend al enviar: aquí solo se guarda la receta y la vista previa.
    const aplicarHonorarios = (datos) => {
        setHonorarios(datos);
        setImagen(null);
        setImagenUrl("");
        if (plantilla) setVariables(variablesPara(plantilla, datos));
    };
    const conHonorarios = modo === "plantilla" && usarHonorarios && !!honorarios;

    // El correo de cobranza llega armado: llena asunto y mensaje (editables) y guarda la receta del PDF.
    const aplicarCobranza = (datos) => {
        setCobranza(datos);
        if (datos) {
            setAsunto(datos.asunto);
            setCuerpo(datos.cuerpo);
        }
    };
    const conCobranza = !esWsp && usarCobranza && !!cobranza;

    const elegirImagen = (archivo) => {
        if (!archivo) return setImagen(null);
        if (!["image/jpeg", "image/png"].includes(archivo.type)) {
            gdAlertify.notiError("La imagen debe ser JPG o PNG.");
            return;
        }
        if (archivo.size > MAX_IMAGEN_MB * 1024 * 1024) {
            gdAlertify.notiError(`La imagen supera ${MAX_IMAGEN_MB} MB (límite de WhatsApp).`);
            return;
        }
        setImagen(archivo);
        setImagenUrl("");
    };

    const seguirEstados = (loteId) => {
        clearInterval(seguimiento.current);
        let veces = 0;
        seguimiento.current = setInterval(async () => {
            veces += 1;
            try {
                // En local el webhook de YCloud no llega: se le pregunta el estado directamente.
                const porConsultar = (resultadoRef.current?.mensajes || [])
                    .filter((m) => m.proveedor_id && ["enviado", "entregado"].includes(m.estado));
                await Promise.all(porConsultar.map((m) => axiosInstance.post(`/envios/mensajes/${m.id}/consultar`).catch(() => null)));

                const { data } = await axiosInstance.get(`/envios/lotes/${loteId}`, { params: { limit: 10 } });
                const items = data?.data?.mensajes?.items || [];
                resultadoRef.current = { ...resultadoRef.current, mensajes: items };
                setResultado((prev) => (prev ? { ...prev, mensajes: items } : prev));
                const terminados = items.every((m) => ["leido", "fallido", "cancelado"].includes(m.estado) || (m.canal_tipo === "email" && m.estado === "enviado"));
                if (terminados || veces >= SEGUIMIENTO_VECES) clearInterval(seguimiento.current);
            } catch {
                clearInterval(seguimiento.current);
            }
        }, SEGUIMIENTO_MS);
    };

    const validar = (lista) => {
        if (!canal) return "Elige un canal.";
        if (!lista.length) return "Indica al menos un destinatario.";
        if (!esWsp) {
            if (lista.some((d) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d))) return "Revisa los correos de destino.";
            if (!asunto.trim() || !cuerpo.trim()) return "El correo necesita asunto y cuerpo.";
            return null;
        }
        if (modo === "plantilla") {
            if (!plantilla) return "Elige una plantilla.";
            if (variables.length !== claves.length || variables.some((v) => !String(v).trim())) {
                return "Completa todas las variables de la plantilla.";
            }
            if (headerConImagen && !imagen && !imagenUrl.trim() && !conHonorarios) {
                return usarHonorarios
                    ? "Elige la auxiliar y espera a que se genere la imagen de la tabla."
                    : "Esta plantilla lleva imagen en el encabezado: sube una o indica su URL.";
            }
            if (headerNoSoportado) return `La prueba no soporta encabezados de tipo ${header.format}.`;
        } else if (!textoLibre.trim() && !imagen && !imagenUrl.trim()) {
            return "Escribe el texto o agrega una imagen.";
        }
        return null;
    };

    const enviar = async () => {
        const lista = destinatarios.map((d) => d.trim()).filter(Boolean);
        const error = validar(lista);
        if (error) {
            gdAlertify.notiError(error);
            return;
        }

        const form = new FormData();
        form.append("canal_id", canalId);
        form.append("destinatarios", JSON.stringify(lista));
        if (!esWsp) {
            form.append("asunto", asunto);
            form.append("cuerpo", cuerpo);
            form.append("plantilla_base", plantillaBase ? "1" : "0");
            if (conCobranza) form.append("cobranza", JSON.stringify(cobranza.cobranza));
        } else {
            form.append("modo", modo);
            if (modo === "plantilla") {
                form.append("plantilla", plantilla.nombre);
                form.append("idioma", plantilla.idioma);
                // Numeradas: lista en orden. Con nombre: objeto { nombre: valor } (Meta exige el nombre).
                form.append("variables", JSON.stringify(variablesParaEnvio(body?.text, claves, variables)));
            } else {
                form.append("cuerpo", textoLibre);
            }
            if (conHonorarios) form.append("honorarios", JSON.stringify(honorarios.honorarios));
            else if (imagen) form.append("imagen", imagen);
            else if (imagenUrl.trim()) form.append("imagen_url", imagenUrl.trim());
        }

        setEnviando(true);
        setResultado(null);
        gdAlertify.showLoader();
        try {
            const { data } = await axiosInstance.post("/envios/prueba", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const r = data?.data || {};
            setResultado(r);
            resultadoRef.current = r;
            const resumen = Object.values(r.resumen || {})[0] || {};
            if (resumen.error_canal) gdAlertify.notiWarning(resumen.error_canal);
            else if (resumen.sin_cupo) gdAlertify.notiWarning("Sin cupo en las últimas 24 h: quedó pendiente.");
            else gdAlertify.notiSuccess("Prueba procesada.");
            if (esWsp && r.lote_id) seguirEstados(r.lote_id);
            onEnviado?.();
        } catch (err) {
            gdAlertify.notiError(mensajeError(err, "No se pudo enviar la prueba."));
        } finally {
            gdAlertify.hideLoader();
            setEnviando(false);
        }
    };

    const vistaPrevia = (body?.text || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (marca, clave) => variables[claves.indexOf(clave)] || marca);

    return (
        <div className="row g-4">
            <div className="col-lg-7">
                <div className="mb-3">
                    <label className="form-label fw-semibold">Canal</label>
                    <select className="form-select" value={canalId} onChange={(e) => cambiarCanal(e.target.value)}>
                        {canales.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.tipo === "whatsapp" ? "WhatsApp" : "Correo"} · {c.nombre}{Number(c.activo) === 1 ? "" : " (inactivo)"}
                            </option>
                        ))}
                    </select>
                    {canal && Number(canal.activo) !== 1 && (
                        <div className="form-text">
                            <i className="bi bi-info-circle me-1"></i>
                            El canal está inactivo: la prueba se envía igual, pero los envíos masivos no salen hasta activarlo en <strong>Canales</strong>.
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold">
                        Destinatarios <small className="text-muted fw-normal">(máximo {MAX_DESTINATARIOS})</small>
                    </label>
                    {destinatarios.map((d, i) => (
                        <input key={i} className="form-control mb-2" value={d}
                            placeholder={esWsp ? (i === 0 ? "+56912345678" : "Opcional: segundo número") : (i === 0 ? "correo@ejemplo.cl" : "Opcional: segundo correo")}
                            onChange={(e) => setDestinatarios(destinatarios.map((x, j) => (j === i ? e.target.value : x)))} />
                    ))}
                    {esWsp && <div className="form-text">Números de Chile: puedes escribir 912345678 y se completa con +56.</div>}
                    <div className="form-text">
                        <i className="bi bi-info-circle me-1"></i>
                        La prueba llega exactamente a los destinatarios que escribas (el modo demo solo desvía los envíos reales).
                    </div>
                </div>

                {!esWsp && canal && (
                    <>
                        <div className="form-check form-switch mb-2">
                            <input className="form-check-input" type="checkbox" id="usar-cobranza" checked={usarCobranza}
                                onChange={(e) => { setUsarCobranza(e.target.checked); if (!e.target.checked) setCobranza(null); }} />
                            <label className="form-check-label" htmlFor="usar-cobranza">
                                Probar un correo de cobranza (tipo de envío, texto con variables y PDF del detalle de un paciente)
                            </label>
                        </div>
                        {usarCobranza && <CobranzaPrueba onListo={aplicarCobranza} />}

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Asunto</label>
                            <input className="form-control" value={asunto} onChange={(e) => setAsunto(e.target.value)} />
                        </div>
                        <div className="mb-2">
                            <label className="form-label fw-semibold">Mensaje</label>
                            <textarea className="form-control" rows={conCobranza ? 10 : 7} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} />
                            <div className="form-text">
                                Texto simple o HTML.{conCobranza && <> Viene armado con el texto del tipo, las variables del paciente y la firma; se adjunta su <strong>PDF del detalle</strong>.</>}
                            </div>
                        </div>
                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="plantilla-base" checked={plantillaBase}
                                onChange={(e) => setPlantillaBase(e.target.checked)} />
                            <label className="form-check-label" htmlFor="plantilla-base">Usar el diseño base de los correos de SKMC</label>
                        </div>
                    </>
                )}

                {esWsp && (
                    <>
                        <div className="btn-group mb-3" role="group">
                            <input type="radio" className="btn-check" id="modo-plantilla" checked={modo === "plantilla"} onChange={() => setModo("plantilla")} />
                            <label className="btn btn-outline-primary" htmlFor="modo-plantilla"><i className="bi bi-card-checklist me-1"></i>Plantilla aprobada</label>
                            <input type="radio" className="btn-check" id="modo-texto" checked={modo === "texto"} onChange={() => setModo("texto")} />
                            <label className="btn btn-outline-primary" htmlFor="modo-texto"><i className="bi bi-chat-text me-1"></i>Texto libre</label>
                        </div>

                        {modo === "plantilla" ? (
                            <>
                                <div className="d-flex gap-2 mb-3">
                                    <select className="form-select" value={plantillaClave} onChange={(e) => elegirPlantilla(e.target.value)} disabled={!plantillas.length}>
                                        <option value="">{plantillas.length ? "Elige una plantilla" : "Primero carga las plantillas"}</option>
                                        {plantillas.map((p) => (
                                            <option key={`${p.nombre}|${p.idioma}`} value={`${p.nombre}|${p.idioma}`} disabled={p.estado !== "APPROVED"}>
                                                {p.nombre} · {p.idioma} · {p.categoria}{p.estado !== "APPROVED" ? ` (${p.estado})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <button className="btn btn-outline-secondary text-nowrap" onClick={cargarPlantillas} disabled={cargandoPlantillas || !canalId}>
                                        {cargandoPlantillas ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-cloud-download me-1"></i>Cargar plantillas</>}
                                    </button>
                                </div>

                                <div className="form-check form-switch mb-2">
                                    <input className="form-check-input" type="checkbox" id="usar-honorarios" checked={usarHonorarios}
                                        onChange={(e) => setUsarHonorarios(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="usar-honorarios">
                                        Llenar con los honorarios de una auxiliar (imagen del Resumen de Pago + variables)
                                    </label>
                                </div>
                                {usarHonorarios && <HonorariosPrueba onListo={aplicarHonorarios} />}
                                {usarHonorarios && honorarios && (
                                    <div className="alert alert-light border py-2 small">
                                        <i className="bi bi-check2-circle text-success me-1"></i>
                                        Datos de <strong>{honorarios.auxiliar}</strong> cargados.
                                        {honorarios.telefonoAuxiliar && <> Su teléfono es {honorarios.telefonoAuxiliar}, pero la prueba va solo a los destinatarios que escribas arriba.</>}
                                    </div>
                                )}

                                {plantilla && plantilla.categoria !== "UTILITY" && (
                                    <div className="alert alert-warning py-2">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        Esta plantilla es <strong>{plantilla.categoria}</strong>. Para avisos de cuenta usa una <strong>UTILITY</strong> (más barata y sin restricciones de marketing).
                                    </div>
                                )}

                                {variables.map((v, i) => (
                                    <div className="input-group mb-2" key={i}>
                                        <span className="input-group-text"><code>{`{{${claves[i]}}}`}</code></span>
                                        <input className="form-control" value={v} placeholder={`Valor de {{${claves[i]}}}`}
                                            onChange={(e) => setVariables(variables.map((x, j) => (j === i ? e.target.value : x)))} />
                                    </div>
                                ))}
                            </>
                        ) : (
                            <>
                                <div className="alert alert-warning py-2">
                                    <i className="bi bi-clock-history me-1"></i>
                                    El texto libre <strong>solo llega si esa persona te escribió por WhatsApp en las últimas 24 h</strong>.
                                    Para escribir en frío hay que usar una plantilla aprobada.
                                </div>
                                <textarea className="form-control mb-3" rows={5} value={textoLibre} onChange={(e) => setTextoLibre(e.target.value)} />
                            </>
                        )}

                        {(modo === "texto" || headerConImagen) && !conHonorarios && (
                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    Imagen {modo === "plantilla" ? "del encabezado" : <small className="text-muted fw-normal">(opcional)</small>}
                                </label>
                                <input type="file" className="form-control mb-2" accept="image/jpeg,image/png" onChange={(e) => elegirImagen(e.target.files?.[0])} />
                                <input className="form-control" placeholder="…o la URL pública de una imagen JPG/PNG" value={imagenUrl}
                                    onChange={(e) => { setImagenUrl(e.target.value); setImagen(null); }} disabled={!!imagen} />
                                <div className="form-text">JPG o PNG, máximo {MAX_IMAGEN_MB} MB. El archivo se sube a YCloud al enviar; no se guarda en el sistema.</div>
                            </div>
                        )}
                    </>
                )}

                <button className="btn btn-success" onClick={enviar} disabled={enviando || !canal}>
                    <i className="bi bi-send me-2"></i>Enviar prueba
                </button>
            </div>

            <div className="col-lg-5">
                {conCobranza && (
                    <div className="mb-4">
                        <h6 className="fw-semibold">Vista previa del correo</h6>
                        <div className="border rounded p-3 bg-white">
                            <div className="small text-muted mb-1">Asunto</div>
                            <div className="fw-semibold mb-3">{asunto}</div>
                            <div dangerouslySetInnerHTML={{ __html: cuerpo }} />
                            <div className="mt-3 small text-muted"><i className="bi bi-paperclip me-1"></i>PDF del detalle de {cobranza.paciente}</div>
                        </div>
                    </div>
                )}

                {esWsp && (modo === "texto" || plantilla) && (
                    <div className="mb-4">
                        <h6 className="fw-semibold">Vista previa</h6>
                        <div className="p-3 rounded" style={{ background: "#e5ddd5" }}>
                            <div className="bg-white rounded shadow-sm p-2" style={{ maxWidth: 320 }}>
                                {vistaImagen && (modo === "texto" || headerConImagen) && (
                                    <img src={vistaImagen} alt="Encabezado" className="img-fluid rounded mb-2" />
                                )}
                                {modo === "plantilla" && headerConImagen && !vistaImagen && (
                                    <div className="bg-light border rounded text-center text-muted py-4 mb-2"><i className="bi bi-image fs-3"></i></div>
                                )}
                                {modo === "plantilla" && String(header?.format || "").toUpperCase() === "TEXT" && (
                                    <div className="fw-bold mb-1">{header.text}</div>
                                )}
                                <div style={{ whiteSpace: "pre-wrap" }}>{modo === "plantilla" ? vistaPrevia : textoLibre}</div>
                                {modo === "plantilla" && footer?.text && <small className="text-muted d-block mt-1">{footer.text}</small>}
                            </div>
                        </div>
                    </div>
                )}

                {resultado && (
                    <div>
                        <h6 className="fw-semibold">Resultado <small className="text-muted fw-normal">(envío #{resultado.lote_id})</small></h6>
                        <ul className="list-group">
                            {(resultado.mensajes || []).map((m) => (
                                <li key={m.id} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{m.destinatario}</span>
                                        <BadgeEstado estado={m.estado} />
                                    </div>
                                    {m.error_detalle && <small className="text-danger d-block mt-1">[{m.error_codigo}] {m.error_detalle}</small>}
                                </li>
                            ))}
                        </ul>
                        <small className="text-muted d-block mt-2">
                            {esWsp
                                ? "Los estados Entregado y Leído llegan por el webhook de YCloud; esta lista se actualiza sola por 2 minutos."
                                : "En correo, Enviado significa que Gmail lo aceptó. Revisa la bandeja de destino."}
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnviosPrueba;
